import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';

interface HashParams {
  [key: string]: string;
}

/**
 * Component to handle hash fragments in URLs and redirect to appropriate routes
 * This is particularly important for Supabase auth callbacks that use hash fragments
 * instead of query parameters (common in password reset links)
 */
const HashRedirectHandler: React.FC = () => {
  const navigate = useNavigate();

  // Helper function to redirect to auth callback with parameters
  const redirectToAuthCallback = (hash: string): void => {
    // Convert hash parameters to query parameters
    const params = new URLSearchParams();
    hash.split('&').forEach((pair: string) => {
      const [key, value] = pair.split('=');
      params.append(key, value);
    });
    
    // Redirect to auth callback with the parameters
    navigate(`/auth/callback?${params.toString()}`);
  };

  useEffect(() => {
    // Check if we have a hash in the URL
    if (window.location.hash) {
      const hash = window.location.hash.substring(1); // Remove the # character
      
      // Parse the hash parameters
      const hashParams: HashParams = {};
      hash.split('&').forEach((pair: string) => {
        const [key, value] = pair.split('=');
        if (key) hashParams[key] = value;
      });
      
      // Check if this is a password reset link with access_token
      if (hashParams.access_token && (hashParams.type === 'recovery' || hash.includes('type=recovery'))) {
        console.log('Detected password reset hash fragment, setting session and redirecting');
        
        // For password reset links, set the session directly and redirect to reset-password-confirm
        if (hashParams.access_token && hashParams.refresh_token) {
          (async () => {
            try {
              // Set the session using the tokens from the hash
              const { data, error } = await supabase.auth.setSession({
                access_token: hashParams.access_token,
                refresh_token: hashParams.refresh_token
              });
              
              if (error) {
                console.error('Error setting session from hash:', error);
                // Fall back to auth callback if direct session setting fails
                redirectToAuthCallback(hash);
              } else {
                console.log('Successfully set session from hash');
                // Direct to reset password page
                navigate('/reset-password-confirm');
              }
            } catch (err) {
              console.error('Error processing hash params:', err);
              // Fall back to auth callback
              redirectToAuthCallback(hash);
            }
          })();
        } else {
          // If we don't have both tokens, fall back to auth callback
          redirectToAuthCallback(hash);
        }
        return;
      }
      
      // Handle other hash fragments that contain access_token (like magic links)
      if (hashParams.access_token) {
        console.log('Detected auth hash fragment, redirecting to auth callback');
        redirectToAuthCallback(hash);
        return;
      }
    }
  }, [navigate]);

  // This component doesn't render anything
  return null;
};

export default HashRedirectHandler;
